import { useQuery } from '@tanstack/react-query';
import { supabaseHarvest } from '@/lib/supabase';
import { getTimeframe, type TimeframeKey } from '@/config/timeframes';
import { bucketLabel } from '@/lib/timeBucket';

export interface StorageGrowthPoint {
  bucket: string;
  bytes: number;
}

export interface StorageGrowthResult {
  buckets: StorageGrowthPoint[];
  /** Bytes collected within the selected window. */
  bytesInWindow: number;
  truncated: boolean;
}

/**
 * Storage growth (bytes added) bucketed across the selected timeframe. Aggregated
 * server-side via the harvest.fn_manifest_timeseries RPC with gap-filling, so the
 * chart always covers the full window with the correct number of x-axis points
 * (replaces the raw-row pull that was silently truncated by PostgREST's max-rows cap).
 */
export function useStorageGrowth(timeframeKey: TimeframeKey) {
  const tf = getTimeframe(timeframeKey);
  return useQuery({
    queryKey: ['storage-growth', tf.key],
    staleTime: 60_000,
    queryFn: async (): Promise<StorageGrowthResult> => {
      const { data, error } = await supabaseHarvest.rpc('fn_manifest_timeseries', {
        p_hours: tf.hours,
        p_bucket_seconds: Math.round(tf.bucketMs / 1000),
      });
      if (error) throw error;
      const rows = (data ?? []) as { bucket_start: string; bytes: number }[];
      let bytesInWindow = 0;
      const buckets = rows.map((r) => {
        const bytes = Number(r.bytes) || 0;
        bytesInWindow += bytes;
        return { bucket: bucketLabel(new Date(r.bucket_start).getTime(), tf.bucketMs), bytes };
      });
      return { buckets, bytesInWindow, truncated: false };
    },
  });
}
